/**
 * LeagueCheck - Componente Principal
 * 
 * Componente orquestador que gestiona la selección y guardado de ligas con filtro por deporte.
 * Obtiene la lista completa de ligas del backend y compara con las seleccionadas actualmente.
 * Permite filtrar ligas por deporte usando tabs y guardar los cambios.
 * 
 * @component
 * @returns {JSX.Element} Contenedor con título, tabs de deportes, lista de ligas y botón guardar
 * 
 */

import { useState } from 'react';
import { postTickerLeaguesSave } from '../../services/tickerLeagues';
import { useLeaguesData } from '../../hooks/useLeaguesData';
import LeagueNotification from './LeagueNotification';
import SportTabs from './SportTabs';
import LeagueList from './LeagueList';
import SaveButton from './SaveButton';
import Loading from '../loading/loading';
import './leagueCheck.sass';


export default function LeagueCheck() {
  const { leagues, loading, error, toggleLeague } = useLeaguesData();
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [activeSport, setActiveSport] = useState('all');

  /**
   * Filtra las ligas según el sport activo
   * Si activeSport es 'all', retorna todas las ligas
   * Si no, retorna solo las ligas del sport seleccionado
   */
  const filteredLeagues = activeSport === 'all'
    ? leagues
    : leagues.filter(league => league.sport === activeSport);

  /**
   * Maneja el guardado de ligas seleccionadas
   * Filtra las ligas con status=true, extrae sus IDs y envía al servidor
   * Muestra notificación de éxito o error
   */
  const handleSave = async () => {
    try {
      setSaving(true);
      const selectedLeagues = leagues.filter(league => league.status);
      const league_ids = selectedLeagues.map(league => league.id);

      const response = await postTickerLeaguesSave(league_ids);

      if (response.statusCode === 200) {
        setNotification({
          message: `Se guardaron ${selectedLeagues.length} liga(s) seleccionada(s) exitosamente`,
          type: 'success'
        });
      } else {
        setNotification({
          message: 'Error al guardar las ligas. Por favor, intenta de nuevo.',
          type: 'error'
        });
      }
    } catch (err) {
      console.error('Error saving leagues:', err);
      setNotification({
        message: 'Error al guardar las ligas. Por favor, intenta de nuevo.',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Cierra la notificación manualmente
   */
  const handleCloseNotification = () => {
    setNotification({ message: '', type: '' });
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <div className="container-league-check"><p className="error-message">{error}</p></div>;
  }

  return (
    <div className="container-league-check">
      <div className='container-league-title'>
        <h1>Listado de Ligas</h1>
      </div>

      <LeagueNotification
        message={notification.message}
        type={notification.type}
        onClose={handleCloseNotification}
      />

      <SportTabs
        leagues={leagues}
        activeSport={activeSport}
        onSportChange={setActiveSport}
      />

      <LeagueList
        leagues={filteredLeagues}
        onToggleLeague={toggleLeague}
      />

      <SaveButton
        onClick={handleSave}
        loading={saving}
      />
    </div>
  );
}